import { useState, useMemo } from "react";
import { NumberField, Stat, Donut, ToolShell } from "./parts";
import { inr, inrShort } from "@/lib/format";

export function HomeLoanEligibility() {
  const [income, setIncome] = useState(120000);
  const [existingEmi, setExistingEmi] = useState(10000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);
  const [foir, setFoir] = useState(50);

  const { eligibleEmi, loan } = useMemo(() => {
    const eligibleEmi = Math.max(0, (income * foir) / 100 - existingEmi);
    const r = rate / 12 / 100;
    const n = years * 12;
    const loan = r === 0 ? eligibleEmi * n : (eligibleEmi * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));
    return { eligibleEmi, loan };
  }, [income, existingEmi, rate, years, foir]);

  return (
    <ToolShell title="Home Loan Eligibility" desc="Find out how much loan you can realistically qualify for based on your income and existing obligations (FOIR method used by banks).">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="space-y-8">
          <NumberField label="Net Monthly Income" value={income} onChange={setIncome} min={20000} max={2000000} step={5000} prefix="₹" testid="elig-income" />
          <NumberField label="Existing Monthly EMIs" value={existingEmi} onChange={setExistingEmi} min={0} max={500000} step={1000} prefix="₹" testid="elig-existing" />
          <NumberField label="FOIR (EMI to income)" value={foir} onChange={setFoir} min={30} max={65} step={1} suffix="%" testid="elig-foir" />
          <NumberField label="Interest Rate (p.a.)" value={rate} onChange={setRate} min={5} max={18} step={0.05} suffix="%" testid="elig-rate" />
          <NumberField label="Tenure" value={years} onChange={setYears} min={1} max={30} step={1} suffix="yrs" testid="elig-tenure" />
        </div>
        <div className="space-y-5">
          <Stat label="Eligible Loan Amount" value={inr(loan)} highlight testid="elig-result" />
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Max Affordable EMI" value={inr(eligibleEmi)} />
            <Stat label="In Words" value={inrShort(loan)} />
          </div>
          <Donut data={[{ name: "Available for EMI", value: eligibleEmi }, { name: "Existing / Buffer", value: existingEmi + (income * (100 - foir)) / 100 }]} />
        </div>
      </div>
    </ToolShell>
  );
}
